import { SEO } from "@/components/SEO";
import { PageSchema } from "@/components/seo/PageSchema";
import { LastUpdated } from "@/components/seo/LastUpdated";
import { TopCTA } from "@/components/cta/TopCTA";
import { MidPageCTA } from "@/components/cta/MidPageCTA";
import { BottomCTA } from "@/components/cta/BottomCTA";
import { FAQSection } from "@/components/content/FAQSection";
import { InternalLinks } from "@/components/content/InternalLinks";
import { Checklist } from "@/components/content/Checklist";
import { DIYvsDoneForYou } from "@/components/content/DIYvsDoneForYou";
import { CalendlyEmbed } from "@/components/CalendlyEmbed";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Search, FileText, MapPin, Gauge, Link2, Star } from "lucide-react";

const faqs = [
  {
    question: "How long does lawn care SEO take to work?",
    answer: "Expect 3-6 months for meaningful results. Local SEO tends to work faster than national SEO. Consistent effort on this checklist will compound over time."
  },
  {
    question: "What's most important for lawn care local SEO?",
    answer: "Google Business Profile optimization, consistent NAP (name, address, phone), getting reviews, and having city-specific pages on your website."
  },
  {
    question: "Do I need to hire an SEO company?",
    answer: "Not necessarily. This checklist covers everything you need to do yourself. However, SEO takes time—if you'd rather focus on running your business, professional help can be valuable."
  },
  {
    question: "How often should I update my website for SEO?",
    answer: "Add new content monthly (blog posts, new service areas, project photos). Review and update existing pages quarterly. Keep your GBP active weekly."
  }
];

const LawnCareWebsiteSEOChecklist = () => {
  const pageUrl = "https://www.leafrushmarketing.com/website-templates/lawn-care-website-seo-checklist/";
  
  return (
    <>
      <SEO
        title="Lawn Care Website SEO Checklist | Complete Guide"
        description="Free SEO checklist for lawn care websites. On-page, technical, and local SEO essentials to rank higher on Google."
        url={pageUrl}
        keywords="lawn care SEO checklist, lawn care website SEO, local SEO lawn care"
      />
      <PageSchema
        pageUrl={pageUrl}
        pageName="Lawn Care Website SEO Checklist"
        pageDescription="Complete SEO checklist for lawn care websites covering on-page, technical, and local SEO."
        faqs={faqs}
      />

      <TopCTA />

      <section className="py-16 md:py-24">
        <div className="container px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <Badge variant="secondary">SEO Checklist</Badge>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
              Lawn Care Website{" "}
              <span className="text-primary">SEO Checklist</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Everything you need to optimize your lawn care website for Google. 
              On-page SEO, technical SEO, and local SEO essentials.
            </p>
            <LastUpdated />
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="container px-4 md:px-6">
          <div className="max-w-4xl mx-auto space-y-8">
            
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary" />
                  On-Page SEO Checklist
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Checklist
                  items={[
                    "Unique title tag for each page (under 60 characters)",
                    "Meta description for each page (under 160 characters)",
                    "One H1 tag per page with main keyword",
                    "H2-H3 subheadings with related keywords",
                    "City/service area mentioned in titles and content",
                    "Alt text on all images (describe the image + location)",
                    "Internal links between related pages",
                    "Phone number in header and footer",
                    "Clear calls-to-action on every page",
                    "Contact form or quote form accessible from all pages"
                  ]}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Gauge className="h-5 w-5 text-primary" />
                  Technical SEO Checklist
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Checklist
                  items={[
                    "Mobile-friendly design (test on Google's Mobile-Friendly Test)",
                    "Page speed under 3 seconds (test on PageSpeed Insights)",
                    "SSL certificate installed (https://)",
                    "XML sitemap submitted to Google Search Console",
                    "No broken links (404 errors)",
                    "Images compressed and optimized",
                    "Consistent URL structure (lowercase, hyphens)",
                    "Google Search Console verified and monitored",
                    "Google Analytics installed",
                    "Schema markup on key pages (LocalBusiness, FAQ)"
                  ]}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-primary" />
                  Local SEO Checklist
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Checklist
                  items={[
                    "Google Business Profile claimed and verified",
                    "GBP fully completed (all sections filled out)",
                    "Correct NAP (name, address, phone) everywhere",
                    "Primary and secondary categories set on GBP",
                    "Service area defined on GBP",
                    "Photos uploaded to GBP (logo, cover, work photos)",
                    "Regular GBP posts (weekly or bi-weekly)",
                    "Review generation strategy in place",
                    "Responding to all reviews (positive and negative)",
                    "Listed on major directories (Yelp, Angi, HomeAdvisor)"
                  ]}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Search className="h-5 w-5 text-primary" />
                  Content & Pages Checklist
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Checklist
                  items={[
                    "Dedicated page for each main service",
                    "Service area pages for each city you serve",
                    "About page with company story and team",
                    "Contact page with form, phone, and address",
                    "Testimonials or reviews page",
                    "FAQ page or FAQs on service pages",
                    "Blog with 1-2 posts per month",
                    "Pricing or 'starting at' information visible",
                    "Before/after gallery of work",
                    "Service area map or list"
                  ]}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Link2 className="h-5 w-5 text-primary" />
                  Link Building Checklist
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Checklist
                  items={[
                    "Listed on local business directories",
                    "Chamber of Commerce membership (if applicable)",
                    "BBB listing",
                    "Industry directories (lawn care associations)",
                    "Local news or blog mentions",
                    "Supplier/partner websites linking to you",
                    "Sponsorships with local sports teams or events",
                    "Guest posts on local blogs",
                    "Social media profiles with website links"
                  ]}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Star className="h-5 w-5 text-primary" />
                  Review Strategy Checklist
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Checklist
                  items={[
                    "System for asking customers for reviews",
                    "Direct link to Google review form",
                    "Review request sent within 24-48 hours of service",
                    "Review reminder for customers who don't respond",
                    "Responding to all Google reviews",
                    "Reviews displayed on website",
                    "Goal of 2-5 new reviews per month",
                    "Handling negative reviews professionally"
                  ]}
                />
              </CardContent>
            </Card>

          </div>
        </div>
      </section>

      <MidPageCTA />

      <section className="py-12">
        <div className="container px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold mb-6">DIY vs. Done-For-You</h2>
            <DIYvsDoneForYou
              diyPoints={[
                "Work through this checklist yourself",
                "Learn SEO basics as you go",
                "Free but time-intensive",
                "Ongoing commitment required"
              ]}
              doneForYouPoints={[
                "We handle all technical and on-page SEO",
                "Monthly reporting and optimization",
                "GBP management included",
                "Focus on your business, not SEO"
              ]}
            />
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="container px-4 md:px-6">
          <div className="max-w-2xl mx-auto">
            <InternalLinks
              title="Related Resources"
              links={[
                { to: "/website-templates/lawn-care-homepage-template/", label: "Lawn Care Homepage Template" },
                { to: "/website-templates/service-area-page-template/", label: "Service Area Page Template" },
                { to: "/resources/google-business-profile-for-lawn-care/", label: "GBP Optimization Guide" },
                { to: "/service-pages-that-rank/", label: "Service Pages That Rank" },
                { to: "/website-templates/", label: "All Templates" }
              ]}
            />
          </div>
        </div>
      </section>

      <FAQSection faqs={faqs} />
      <BottomCTA />
      <CalendlyEmbed />
    </>
  );
};

export default LawnCareWebsiteSEOChecklist;
